import React, { useState, useEffect, useRef, useContext } from 'react'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'

import MainContentLayout from '../../Layout/MainContentLayout'
import {
  DateFilterTemplate,
  InputFieldTemplate,
  MultiSelectTemplate,
  SingleDateFilterTemplate,
} from '../../shared/FilterTemplates'
import server from '../../../server'
import { formatCurrency, formatDate } from '../../shared/utils'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'
import ReportScopeSummary from './ReportScopeSummary'
import { ORDER_STATUS_OPTIONS, buildReportParams } from './reportFilters'
export default function ManufacturersReports() {
  const dt = useRef(null)

  const { module: systemModule, user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [manufacturers, setManufacturers] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [state, setState] = useState(() =>
    getInitialPaginationState(user, {
      sortField: '',
      sortOrder: '',
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        'manufacturer.name': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        totalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        totalRefundedAmount: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
      },
    })
  )
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  // Empty = this report's default scope (confirmed and received orders).
  // Cancelled is never an option.
  const [statuses, setStatuses] = useState([])
  const [scope, setScope] = useState(null)

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const getData = () => {
    setTableLoading(true)

    const params = buildReportParams({
      startDate,
      endDate,
      statuses,
      formatDate,
    })

    server
      .get(`/manufacturers/pharmacy${params}`)
      .then((response) => {
        setManufacturers(response.data.Manufacturers)
        setScope({ criteria: response.data.criteria })
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
        // setError(error.response.data.message)
      })
  }

  useEffect(() => {
    checkClearBtn()
    return getData()
  }, [state, startDate, endDate, statuses])

  const checkClearBtn = () => {
    if (state.sortField || state.sortOrder || statuses.length > 0) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in state?.filters) {
      if (key === 'global') {
        continue
      } else if (
        state?.filters[key].constraints[0].value ||
        state?.filters[key]?.constraints[1]?.value
      ) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const clearFilter = () => {
    initState()
  }

  const initState = () => {
    setState((prevState) => ({
      ...prevState,
      first: 0,
      page: 0,
      rows: defaultRows,
      sortField: '',
      sortOrder: '',
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        'manufacturer.name': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        totalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        totalRefundedAmount: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
      },
    }))
    setStartDate(null)
    setEndDate(null)
    setStatuses([])
  }

  // TOOLBAR
  const topLeftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={clearFilter}
      disabled={clearBtnDisabled}
    />
  )

  const topRightToolbarTemplate = () => (
    <>
      <div className='d-flex gap-1 align-items-center flex-wrap justify-content-end'>
        <MultiSelectTemplate
          state={statuses}
          stateHandler={setStatuses}
          options={ORDER_STATUS_OPTIONS}
          optionValue='value'
          placeholder='Default Statuses'
          maxSelectedLabels={2}
          showClear
        />
        <SingleDateFilterTemplate
          state={startDate}
          stateHandler={(e) => setStartDate(e.value)}
          placeholder='From'
          showIcon={false}
        />
        <SingleDateFilterTemplate
          state={endDate}
          stateHandler={(e) => setEndDate(e.value)}
          placeholder='To'
          disabled={!startDate}
          minDate={startDate}
        />
      </div>
    </>
  )

  // BODY TEMPLATES
  const orderAmountBodyTemplate = (order) =>
    formatCurrency(order.totalExtendedPrice)

  const paAmountBodyTemplate = (order) =>
    formatCurrency(order.totalRefundedAmount)

  // FILTER TEMPLATES
  const manufacturerFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Manufacturer'
      icon='mdi mdi-cog'
    />
  )
  const orderAmountFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const paAmountFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )

  return (
    <MainContentLayout title='Manufacturers Report' dt={dt}>
      <Toolbar
        className='mb-2'
        left={topLeftToolbarTemplate}
        right={topRightToolbarTemplate}
      />
      {scope?.criteria && (
        <ReportScopeSummary
          criteria={scope.criteria}
          excludedByStatus={(scope.criteria.statusesExcluded || []).map(
            (status) => ({ status })
          )}
        />
      )}
      <DataTable
        ref={dt}
        value={manufacturers}
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        paginator
        showGridlines
        loading={tableLoading}
        dataKey='manufacturer.name'
        removableSort
        globalFilterFields={['name']}
        emptyMessage='No Manufactures Found.'
        sortField={state.sortField}
        sortOrder={state.sortOrder}
        filters={state.filters}
        rows={state.rows}
        first={state.first}
        onPage={(e) => setState((prev) => applyPageEvent(prev, e))}
        onSort={(e) => {
          e.first = 0
          setState(e)
        }}
        onFilter={(e) => setState(e)}
      >
        <Column
          field='manufacturer.name'
          header='Manufacturer Name'
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={manufacturerFilterElement}
        />
        <Column
          field='totalExtendedPrice'
          header='Total Order Amount'
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={orderAmountFilterElement}
          body={orderAmountBodyTemplate}
        />
        {systemModule !== 'Origins Direct' && (
          <Column
            field='totalRefundedAmount'
            header='Total PA Amount'
            sortable
            filter
            showFilterMenuOptions={false}
            filterElement={paAmountFilterElement}
            body={paAmountBodyTemplate}
          />
        )}
      </DataTable>
    </MainContentLayout>
  )
}
